import React, { useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const PAGE_WIDTH  = 460;
const PAGE_HEIGHT = 650;

const PdfPageWrapper = React.forwardRef(({ pageNumber }, ref) => (
    <div ref={ref} style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, background: 'white', overflow: 'hidden' }}>
        <Page pageNumber={pageNumber} width={PAGE_WIDTH} renderAnnotationLayer={false} renderTextLayer={false} />
    </div>
));

const CtrlBtn = ({ onClick, disabled, children }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '6px',
            padding: '5px 14px',
            cursor: disabled ? 'default' : 'pointer',
            fontSize: '0.88rem',
            fontWeight: '600',
            opacity: disabled ? 0.4 : 1,
            transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!disabled) e.target.style.background = 'rgba(255,255,255,0.28)'; }}
        onMouseLeave={e => { e.target.style.background = disabled ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.15)'; }}
    >
        {children}
    </button>
);

const PdfFlipModal = ({ show, onHide, pdfUrl, title }) => {
    const [numPages, setNumPages] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [zoom, setZoom] = useState(1);
    const bookRef = useRef();

    const prevPage = () => bookRef.current?.pageFlip().flipPrev();
    const nextPage = () => bookRef.current?.pageFlip().flipNext();
    const zoomIn   = () => setZoom(z => Math.min(+(z + 0.15).toFixed(2), 2));
    const zoomOut  = () => setZoom(z => Math.max(+(z - 0.15).toFixed(2), 0.5));

    const handleHide = () => {
        setNumPages(null);
        setCurrentPage(0);
        setZoom(1);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleHide} centered dialogClassName="pdf-flip-dialog">
            {/* Header */}
            <div style={{ background: '#1a2a4a', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderRadius: '8px 8px 0 0' }}>
                <span style={{ color: 'white', fontSize: '0.9rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {title}
                </span>
                <a
                    href={pdfUrl}
                    download
                    style={{ background: 'white', color: '#1a2a4a', fontWeight: '700', border: 'none', borderRadius: '6px', padding: '5px 14px', textDecoration: 'none', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                >
                    ⬇ Descargar
                </a>
                <button
                    onClick={handleHide}
                    style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.2rem', cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
                >
                    ✕
                </button>
            </div>

            {/* PDF viewer - fixed height, scrollable cuando hay zoom */}
            <div style={{ background: '#3a3a3a', overflow: 'auto', flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px' }}>
                <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    loading={<p style={{ color: 'white', marginTop: '40px' }}>Cargando PDF...</p>}
                    error={<p style={{ color: '#f88', marginTop: '40px' }}>No se pudo cargar el PDF.</p>}
                >
                    {numPages && (
                        <div style={{ zoom }}>
                            <HTMLFlipBook
                                width={PAGE_WIDTH}
                                height={PAGE_HEIGHT}
                                ref={bookRef}
                                showCover={false}
                                mobileScrollSupport={true}
                                onFlip={(e) => setCurrentPage(e.data)}
                                style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}
                            >
                                {Array.from({ length: numPages }, (_, i) => (
                                    <PdfPageWrapper key={i} pageNumber={i + 1} />
                                ))}
                            </HTMLFlipBook>
                        </div>
                    )}
                </Document>
            </div>

            {/* Barra de controles */}
            {numPages && (
                <div style={{ background: '#2a2a2a', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', borderRadius: '0 0 8px 8px', flexWrap: 'wrap' }}>
                    <CtrlBtn onClick={prevPage} disabled={currentPage === 0}>◀ Anterior</CtrlBtn>
                    <span style={{ color: '#ccc', fontSize: '0.88rem', minWidth: '90px', textAlign: 'center' }}>
                        {currentPage + 1} / {numPages}
                    </span>
                    <CtrlBtn onClick={nextPage} disabled={currentPage >= numPages - 1}>Siguiente ▶</CtrlBtn>

                    <span style={{ color: '#555', margin: '0 6px' }}>|</span>

                    <CtrlBtn onClick={zoomOut} disabled={zoom <= 0.5}>−</CtrlBtn>
                    <span style={{ color: '#ccc', fontSize: '0.88rem', minWidth: '42px', textAlign: 'center' }}>
                        {Math.round(zoom * 100)}%
                    </span>
                    <CtrlBtn onClick={zoomIn} disabled={zoom >= 2}>+</CtrlBtn>
                </div>
            )}
        </Modal>
    );
};

export default PdfFlipModal;
